import { Provider } from '@repo/api-gw-sdk';
import type { NextRequest } from 'next/server';

import { parseJson } from '@/utils/string';

export const providerAndMarketplaceTokenCookieName = 'mptoken';
export const sessionTokenCookieName = 'next-auth.session-token';
export const securedSessionTokenCookieName = `__Secure-${sessionTokenCookieName}`;
export const globalManagementAccessCookieName = 'eon-global-management-access';
export const demoSettingsCookieName = 'eon-demo-settings';
export const accountDomainCookieName = 'ead';
export const docsBaseUrl = 'https://docs.eon.io';

export const getGlobalManagementAccessCookieExpiration = () =>
  new Date(new Date().getTime() + 1000 * 60 * 60 * 2); // 2 hours
export const getDemoSettingsCookieExpiration = () =>
  new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 3); // 3 days

export const knownPublicDomains = [
  'console.eon.io',
  'console.app.eon.io',
  'testing-console.eon.io',
  'testing-console.app.eon.io',
  'dev-console.app.eon.io',
  'staging-console.eon.io',
];

export const getHostFromHeaders = (headerList: Pick<Headers, 'get'>): string =>
  headerList.get('x-forwarded-host') || headerList.get('host') || '';

type LoginHostKind = 'public-root' | 'public-tenant' | 'custom';

export const getLoginHostKind = (host: string): LoginHostKind => {
  if (knownPublicDomains.includes(host)) {
    return 'public-root';
  }
  if (knownPublicDomains.some((domain) => host.endsWith(`.${domain}`))) {
    return 'public-tenant';
  }
  return 'custom';
};

// A tenant (account domain) label is used as a DNS subdomain: alphanumerics and
// interior hyphens only (no leading/trailing hyphen), at least one character.
export const isValidTenant = (tenant: string): boolean =>
  /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?$/.test(tenant);

// Local dev only: when the console is served from a `<tenant>.localhost` subdomain
// but the API base points at bare `localhost`, retarget the base to the SAME
// subdomain host (keeping the port). Auth cookies are host-only and port-agnostic,
// so this lands the bypass cookie on the console's host, where getAuthToken can
// read it — mirroring how bare `localhost:3000` shares cookies with `localhost:8080`.
// No-op for a relative base (mock mode), bare `localhost`, or any non-localhost host.
export const localSubdomainApiBaseUrl = (
  baseUrl: string,
  host: string
): string => {
  let base: URL;
  try {
    base = new URL(baseUrl);
  } catch {
    return baseUrl;
  }
  const consoleHost = host.split(':')[0].toLowerCase();
  const baseHost = base.hostname.toLowerCase();
  const bothLocalhost =
    (baseHost === 'localhost' || baseHost.endsWith('.localhost')) &&
    consoleHost.endsWith('.localhost');
  if (bothLocalhost && consoleHost !== baseHost) {
    base.hostname = consoleHost;
    return base.origin;
  }
  return baseUrl;
};

const docsOrigin = new URL(docsBaseUrl).origin;

// A post-login redirect target safe to use as-is: a same-origin relative path
// (protocol-relative `//evil` and other absolute URLs are rejected) or an
// absolute docs.eon.io URL (the docs login-return flow). Anything else falls
// back to the app root.
export const safeReturnUrl = (raw: string | null | undefined): string => {
  if (raw && raw.startsWith('/') && !raw.startsWith('//')) {
    return raw;
  }
  try {
    if (raw && new URL(raw).origin === docsOrigin) {
      return raw;
    }
  } catch {
    // not a parseable absolute URL — fall through to the safe default
  }
  return '/';
};

// The public-root host that tenant subdomains hang off, recovered from a host
// that may already carry one: 'acme.console.eon.io' => 'console.eon.io'. A host
// that is itself a public root is returned unchanged. For anything unrecognised
// (custom domains, `<tenant>.localhost:3000`) the first label is dropped, which
// is the only reading available without a registry of custom domains.
export const publicRootHost = (host: string): string => {
  const known = knownPublicDomains.find(
    (domain) => host === domain || host.endsWith(`.${domain}`)
  );
  if (known) {
    return known;
  }
  const [, ...rest] = host.split('.');
  return rest.length > 0 ? rest.join('.') : host;
};

// Re-attach a tenant as a subdomain of the public-root host, e.g.
// tenantUrl('https', 'acme', 'console.eon.io', '/login?x=1')
//   => 'https://acme.console.eon.io/login?x=1'.
// `protocol` may be 'https' or 'https:' (the trailing colon from URL.protocol /
// location.protocol is normalised away).
export const tenantUrl = (
  protocol: string,
  tenant: string,
  host: string,
  pathAndQuery: string
): string => `${protocol.replace(/:$/, '')}://${tenant}.${host}${pathAndQuery}`;

export interface Environment {
  isDemo: boolean;
  isDev: boolean;
  isTest: boolean;
  enableOrgDemo: boolean;
  enableForceLogoutOn401: boolean;
  clientApiBaseUrl: string;
  serviceAccountId: string;
  gcpControlPlane: string;
  gitCommit: string;
  launchDarklyClientId: string | undefined;
  posthogKey: string | undefined;
  consoleAssetPrefix: string;
  consoleReleasesBaseUrl: string;
}

export interface DemoSettings {
  [Provider.Aws]: boolean;
  [Provider.Azure]: boolean;
  [Provider.Gcp]: boolean;
  [Provider.AzureEntraId]: boolean;
  logSpace: boolean;
  ransomware: boolean;
  compliance: boolean;
  dataPlatform: boolean;
  primaryProvider: Provider.Aws | Provider.Azure | Provider.Gcp;
}

export const defaultDemoSettings: DemoSettings = {
  [Provider.Aws]: true,
  [Provider.Azure]: true,
  [Provider.Gcp]: true,
  [Provider.AzureEntraId]: false,
  logSpace: false,
  ransomware: false,
  compliance: false,
  dataPlatform: false,
  primaryProvider: Provider.Aws,
};

// All `process.env` reads live in `serverEnv.ts` (server-only vars, secrets, and
// the `Environment` config assembled by getEnvironment()). Client-facing values —
// including `launchDarklyClientId`, `posthogKey`, `consoleAssetPrefix`, and
// `consoleReleasesBaseUrl` — are exposed on `Environment` and consumed via
// `useEnvironment()`. This module holds only types, constants, and pure helpers.

export const getDemoSettings = (req: NextRequest) => {
  const cookie = req.cookies.get(demoSettingsCookieName);
  return getDemoSettingsFromCookie(cookie?.value);
};

export const getDemoSettingsFromCookie = (
  cookieValue: string | undefined | null
) => {
  if (cookieValue) {
    const demoSettings = parseJson<DemoSettings>(cookieValue);
    if (demoSettings) {
      return {
        ...defaultDemoSettings,
        ...demoSettings,
      };
    }
  }

  return defaultDemoSettings;
};
